import React, { useContext, useEffect, useState } from 'react'

import { Box } from '@mui/material'

import APIS from "../../../../../utils/APIS"
import SuperAdminContext from "../../../../../contexts/SuperAdminContext"
import LanguageContext from "../../../../../contexts/LanguageContext"
import { useTexte } from "../../../../../contexts/FormeAdresseContext"
import {
  categoriesDuModule, ponderationDuModule, categorieCochee, questionsSansCategorie,
  enregistrerPonderation, MESSAGES_ECHEC, OUI, NON,
} from "../../../../../utils/ponderationModule"
import { SEUIL_SCORE_CERTIFICAT } from "../../../Certificat/moduleTermine"

/* ⛔ PANNEAU DE LA SLIDE « FIN DE MODULE » (07/10/2026, fiche Notion de Nawal,
   partie B, solution mixte).

   Les cases sont le reglage EXISTANT du module, « glbMaster pondération »
   (`glbMasterPond`) : la meme donnee que dans « Info sur le module ». Cocher
   ici l'enregistre tout de suite, par le chemin du formulaire du module
   (utils/ponderationModule.js) ; ce que le formulaire enregistre se voit ici,
   car la slide relit le module en s'ouvrant dans l'editeur.
   Un module sans slide « Fin de module » garde sa ponderation : rien n'est
   ecrit tant qu'on ne coche pas. */

/* Pose la ponderation ENREGISTREE dans l'editeur : le module affiche, ET sa
   copie de reference (`saveFormationAdmin`), sinon le bouton « Sauvegarder »
   de l'editeur la renverrait par un autre chemin. Le reste des modifications
   en cours de l'editeur n'est pas touche. */
export const appliquerPonderation = (admin, idModule, glbMasterPond) => {
  admin?.setContent?.((prec) => (prec && prec.id === idModule && prec.glbMasterPond !== glbMasterPond)
    ? { ...prec, glbMasterPond }
    : prec)
  try {
    const reference = JSON.parse(sessionStorage.getItem('saveFormationAdmin'))
    if (reference && reference.id === idModule && reference.glbMasterPond !== glbMasterPond) {
      reference.glbMasterPond = glbMasterPond
      sessionStorage.setItem('saveFormationAdmin', JSON.stringify(reference))
    }
  } catch (e) { /* pas de reference : rien a aligner */ }
}

/* La slide, ouverte dans l'editeur, relit la ponderation du module : celle que
   le formulaire a pu changer depuis l'ouverture de l'editeur (qui ne recharge
   pas un module deja ouvert). Lecture seule. */
export const useRelirePonderation = (idModule, actif) => {
  const admin = useContext(SuperAdminContext)
  useEffect(() => {
    if (!actif || idModule === undefined || idModule === null || !admin?.setContent) return undefined
    let vivant = true
    Promise.resolve(APIS.getContentByIdForAdmin({ id: idModule }))
      .then((module) => {
        if (!vivant || !module || module.id !== idModule) return
        appliquerPonderation(admin, idModule, module.glbMasterPond ?? null)
      })
      .catch(() => {})
    return () => { vivant = false }
  }, [idModule, actif])
}

const TITRE = { marginBottom:"2px", fontWeight:"bold" }
const CADRE = { border:"1px solid #2929292f", padding:"5px" }
const NOTE = { fontSize:"11px", color:"#7C7C7C" }

const FinDeModuleHelper = ({ actFormation, format, apercu, setApercu, etats, exemples }) => {
  const admin = useContext(SuperAdminContext)
  const languageValues = useContext(LanguageContext)?.languageValues
  /* le titre par defaut, au tu ou au vous selon la cible du module (le panneau
     est rendu dans le FormeAdresseProvider du lecteur de l'admin) */
  const t = useTexte()
  /* ⚠️ ON FUSIONNE `answers`, on ne l'ecrase pas (meme regle que DiagHelper) :
     il porte aussi les deux textes de la slide. */
  const answers = (format?.answers && typeof format.answers === "object") ? format.answers : {}
  const changerTitre = (valeur) => {
    admin?.onSequenceChange?.("answers", { ...answers, titreResultat: valeur }, admin?.adminPlayerIndexes)
  }
  const categories = categoriesDuModule(actFormation)
  const ponderation = ponderationDuModule(actFormation)
  const cochees = categories.filter((c) => categorieCochee(ponderation, c.key))
  const points = cochees.reduce((somme, c) => somme + c.max, 0)
  const sansCategorie = questionsSansCategorie(actFormation)
  const [enCours, setEnCours] = useState(null)

  const cocher = async (key, coche) => {
    if (enCours) return
    setEnCours(key)
    admin?.setOverlayForSave?.(true)
    try {
      const r = await enregistrerPonderation({ idModule: actFormation?.id, key, value: coche ? OUI : NON })
      if (r.ok) {
        appliquerPonderation(admin, actFormation?.id, r.glbMasterPond)
        /* comme le formulaire apres son enregistrement */
        if (r.result?.reloadGroupes === true && r.result?.groupes) admin?.setGroupes?.(r.result.groupes)
        admin?.updateAdminContents?.(r.archive)
      } else {
        window.alert(MESSAGES_ECHEC[r.raison] || MESSAGES_ECHEC.enregistrement)
      }
    } catch (e) {
      window.alert(MESSAGES_ECHEC.enregistrement)
    } finally {
      admin?.setOverlayForSave?.(false)
      setEnCours(null)
    }
  }

  const LIBELLES_APERCU = [
    [etats.valide, "Validé"],
    [etats.pasEncoreValide, "Pas encore validé"],
    [etats.sansScore, "Sans score de module"],
  ]

  return (
    <>
      {/* Le titre en haut a gauche de la slide, comme « Titre en haut de la
          page » du Diagnostic. Vide : le titre par defaut, au tu ou au vous
          selon la cible du module. */}
      <Box sx={TITRE}>Titre en haut de la page</Box>
      <Box sx={{...CADRE, marginBottom:"10px"}}>
        <textarea
          data-titre-fin-module
          value={typeof answers.titreResultat === "string" ? answers.titreResultat : ""}
          placeholder={t(languageValues?.formation?.finModuleTitre) || ""}
          onChange={(e) => { changerTitre(e.target.value) }}
          rows={2}
          style={{width:"100%", boxSizing:"border-box", fontFamily:"inherit", fontSize:"12px"}}
        />
        <Box sx={NOTE}>
          Laissé vide, la slide affiche le titre par défaut (ci-dessus en gris), au tu ou au vous selon la cible du module.
        </Box>
      </Box>

      <Box sx={TITRE}>Score du module (glbMaster)</Box>
      <Box sx={CADRE} data-panneau-ponderation>
        <Box sx={{...NOTE, marginBottom:"6px"}}>
          Cochez les catégories qui comptent dans le score du module. C'est le réglage
          «&nbsp;glbMaster pondération&nbsp;» du module («&nbsp;Info sur le module&nbsp;»)&nbsp;: la même donnée,
          modifiée ici ou là-bas. Ce score sert aussi à «&nbsp;Mon résultat&nbsp;» et au certificat.
          Enregistré dès le clic.
        </Box>
        {categories.length === 0 &&
          <Box>Aucune catégorie dans ce module&nbsp;: pas de score de module.</Box>
        }
        <Box sx={{display:"flex", flexDirection:"column", gap:"4px"}}>
          {categories.map(({ key, max }) => (
            <Box component="label" key={key} sx={{display:"flex", alignItems:"center", gap:"6px", cursor: enCours ? "wait" : "pointer"}}>
              <input
                type="checkbox"
                name={key}
                data-ponderation={key}
                checked={categorieCochee(ponderation, key)}
                disabled={!!enCours}
                onChange={(e) => { cocher(key, e.target.checked) }}
                style={{display:"block", margin:0}}
              />
              <Box component="span">{key}</Box>
              <Box component="span" sx={{color:"#7C7C7C"}}>({max}&nbsp;pts max)</Box>
            </Box>
          ))}
        </Box>
        {categories.length > 0 &&
          <Box sx={{marginTop:"6px"}} data-points-coches>
            Score du module sur {points}&nbsp;points ({cochees.length}&nbsp;catégorie{cochees.length > 1 ? "s" : ""} cochée{cochees.length > 1 ? "s" : ""})
          </Box>
        }
        {categories.length > 0 && cochees.length === 0 &&
          <Box sx={{marginTop:"6px", color:"#B23A3A"}} data-sans-score-de-module>
            Aucune catégorie cochée&nbsp;: pas de score de module, donc ni «&nbsp;Validé&nbsp;» ni certificat.
            L'apprenant voit le score global, sans rosette.
          </Box>
        }
        {sansCategorie > 0 &&
          <Box sx={{...NOTE, marginTop:"6px"}}>
            {sansCategorie}&nbsp;question{sansCategorie > 1 ? "s" : ""} sans catégorie («&nbsp;master&nbsp;»)&nbsp;:
            elle{sansCategorie > 1 ? "s" : ""} ne compte{sansCategorie > 1 ? "nt" : ""} dans aucun score de module.
          </Box>
        }
      </Box>

      <Box sx={{...TITRE, marginTop:"10px"}}>Aperçu dans la slide</Box>
      <Box sx={CADRE}>
        <Box sx={{display:"flex", flexWrap:"wrap", gap:"6px"}}>
          {LIBELLES_APERCU.map(([etat, libelle]) => (
            <Box
              key={etat}
              component="button"
              type="button"
              data-apercu={etat}
              onClick={() => setApercu(etat)}
              sx={{
                font:"inherit", fontSize:"12px", padding:"3px 8px", cursor:"pointer",
                border:"1px solid #292929", borderRadius:0,
                backgroundColor: apercu === etat ? "#292929" : "#FFFFFF",
                color: apercu === etat ? "#FFFFFF" : "#292929",
              }}
            >
              {libelle}
            </Box>
          ))}
        </Box>
        <Box sx={{...NOTE, marginTop:"6px"}}>
          Score d'exemple ({exemples[apercu]}&nbsp;%), pas un vrai résultat&nbsp;: l'apprenant voit le
          sien. Validé au-dessus de {SEUIL_SCORE_CERTIFICAT}&nbsp;%, comme le certificat. Les deux textes s'écrivent
          directement dans la slide, sous le titre. «&nbsp;Validé&nbsp;»&nbsp;: rien tant qu'il est vide.
          «&nbsp;Pas encore validé&nbsp;»&nbsp;: vide, l'annonce du rattrapage (en gris dans la slide).
        </Box>
      </Box>
    </>
  )
}

export default FinDeModuleHelper
